import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SubscriptionTierIndicator from '../../components/ui/SubscriptionTierIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

// Import all components
import InventoryTable from './components/InventoryTable';
import BulkUploadPanel from './components/BulkUploadPanel';
import AlertConfigurationPanel from './components/AlertConfigurationPanel';
import JobMonitorPanel from './components/JobMonitorPanel';
import QuickStatsCards from './components/QuickStatsCards';

const InventoryManagement = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [activePanel, setActivePanel] = useState('upload'); // upload, alerts, monitor
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState({});

  // Mock inventory data
  useEffect(() => {
    const mockInventory = [
      {
        id: 1,
        make: "Caterpillar",
        model: "336E",
        year: 2018,
        hours: 2500,
        serialNumber: "CAT123456789",
        capacity: 36,
        boomLength: 60,
        bookValue: 285000,
        marketValue: 265000,
        lastValuation: new Date(Date.now() - 86400000 * 5),
        alertStatus: "active",
        condition: 4,
        location: "Dallas, TX"
      },
      {
        id: 2,
        make: "Komatsu",
        model: "PC350",
        year: 2019,
        hours: 1800,
        serialNumber: "KOM987654321",
        capacity: 35,
        boomLength: 58,
        bookValue: 320000,
        marketValue: 298000,
        lastValuation: new Date(Date.now() - 86400000 * 3),
        alertStatus: "triggered",
        condition: 5,
        location: "Houston, TX"
      },
      {
        id: 3,
        make: "Liebherr",
        model: "LTM 1090-4.2",
        year: 2020,
        hours: 1200,
        serialNumber: "LIE456789123",
        capacity: 90,
        boomLength: 164,
        bookValue: 850000,
        marketValue: 825000,
        lastValuation: new Date(Date.now() - 86400000 * 7),
        alertStatus: "inactive",
        condition: 5,
        location: "Austin, TX"
      },
      {
        id: 4,
        make: "Grove",
        model: "RT9150E",
        year: 2017,
        hours: 3200,
        serialNumber: "GRV789123456",
        capacity: 150,
        boomLength: 197,
        bookValue: 1200000,
        marketValue: 1050000,
        lastValuation: new Date(Date.now() - 86400000 * 2),
        alertStatus: "active",
        condition: 3,
        location: "San Antonio, TX"
      },
      {
        id: 5,
        make: "Tadano",
        model: "GR-1000XL",
        year: 2021,
        hours: 800,
        serialNumber: "TAD321654987",
        capacity: 100,
        boomLength: 174,
        bookValue: 950000,
        marketValue: 920000,
        lastValuation: new Date(Date.now() - 86400000 * 1),
        alertStatus: "active",
        condition: 5,
        location: "Fort Worth, TX"
      },
      {
        id: 6,
        make: "Manitowoc",
        model: "MLC300",
        year: 2016,
        hours: 4500,
        serialNumber: "MAN654987321",
        capacity: 300,
        boomLength: 276,
        bookValue: 2100000,
        marketValue: 1850000,
        lastValuation: new Date(Date.now() - 86400000 * 10),
        alertStatus: "triggered",
        condition: 3,
        location: "Oklahoma City, OK"
      },
      {
        id: 7,
        make: "Link-Belt",
        model: "210X4",
        year: 2019,
        hours: 2100,
        serialNumber: "LNK147258369",
        capacity: 21,
        boomLength: 48,
        bookValue: 195000,
        marketValue: 185000,
        lastValuation: new Date(Date.now() - 86400000 * 4),
        alertStatus: "active",
        condition: 4,
        location: "Little Rock, AR"
      },
      {
        id: 8,
        make: "Terex",
        model: "RT780",
        year: 2018,
        hours: 2800,
        serialNumber: "TRX963852741",
        capacity: 80,
        boomLength: 142,
        bookValue: 680000,
        marketValue: 625000,
        lastValuation: new Date(Date.now() - 86400000 * 6),
        alertStatus: "inactive",
        condition: 4,
        location: "Shreveport, LA"
      }
    ];

    setInventory(mockInventory);

    // Calculate stats
    const totalValue = mockInventory?.reduce((sum, item) => sum + item?.marketValue, 0);
    const underpricedItems = mockInventory?.filter(item => item?.marketValue < item?.bookValue * 0.9)?.length;
    const activeAlerts = mockInventory?.filter(item => item?.alertStatus === 'active')?.length;

    setStats({
      totalItems: mockInventory?.length,
      totalItemsChange: 12.5,
      totalValue: totalValue,
      totalValueChange: 8.3,
      underpricedItems: underpricedItems,
      underpricedItemsChange: -15.2,
      activeAlerts: activeAlerts,
      activeAlertsChange: 25.0
    });
  }, []);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/crane-search-dashboard' },
    { label: 'Inventory Management' }
  ];

  const handleSelectionChange = (newSelection) => {
    setSelectedItems(newSelection);
  };

  const handleRevaluate = (itemIds) => {
    console.log('Revaluating items:', itemIds);
    // Mock revaluation process
    alert(`Starting revaluation for ${itemIds?.length} items. Check Job Monitor for progress.`);
  };

  const handleConfigureAlert = (itemIds) => {
    setActivePanel('alerts');
    console.log('Configuring alerts for items:', itemIds);
  };

  const handleViewDetails = (itemId) => {
    window.location.href = `/crane-valuation-report?id=${itemId}`;
  };

  const handleUploadComplete = (result) => {
    console.log('Upload completed:', result);
    alert(`Upload completed! ${result?.successful} items processed successfully, ${result?.errors} errors.`);
  };

  const handleSaveAlert = (alertConfig) => {
    console.log('Alert saved:', alertConfig);
    alert('Alert configuration saved successfully!');
  };

  const handleAddSingleCrane = () => {
    window.location.href = '/crane-search-dashboard?mode=add';
  };

  const handleExportAll = () => {
    console.log('Exporting all inventory...');
    alert('Export started. Check Job Monitor for progress.');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar />
      <main className="container mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
            <div>
              <Breadcrumbs items={breadcrumbItems} className="mb-2" />
              <h1 className="text-3xl font-bold text-text-primary">Inventory Management</h1>
              <p className="text-text-secondary mt-2">
                Manage your crane inventory, configure alerts, and track bulk operations
              </p>
            </div>
            
            <div className="flex items-center space-x-4 mt-4 lg:mt-0">
              <SubscriptionTierIndicator tier="pro" />
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  onClick={handleAddSingleCrane}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Crane
                </Button>
                <Button
                  variant="outline"
                  onClick={handleExportAll}
                  iconName="Download"
                  iconPosition="left"
                >
                  Export All
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <QuickStatsCards stats={stats} className="mb-8" />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Inventory Table - Takes 2 columns on xl screens */}
          <div className="xl:col-span-2">
            <InventoryTable
              inventory={inventory}
              selectedItems={selectedItems}
              onSelectionChange={handleSelectionChange}
              onRevaluate={handleRevaluate}
              onConfigureAlert={handleConfigureAlert}
              onViewDetails={handleViewDetails}
            />
          </div>

          {/* Side Panels - Takes 1 column on xl screens */}
          <div className="space-y-6">
            {/* Panel Selector */}
            <div className="bg-surface border border-border rounded-lg shadow-card p-4">
              <div className="flex space-x-2">
                <Button
                  variant={activePanel === 'upload' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActivePanel('upload')}
                  iconName="Upload"
                  iconPosition="left"
                  className="flex-1"
                >
                  Upload
                </Button>
                <Button
                  variant={activePanel === 'alerts' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActivePanel('alerts')}
                  iconName="Bell"
                  iconPosition="left"
                  className="flex-1"
                >
                  Alerts
                </Button>
                <Button
                  variant={activePanel === 'monitor' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActivePanel('monitor')}
                  iconName="Activity"
                  iconPosition="left"
                  className="flex-1"
                >
                  Jobs
                </Button>
              </div>
            </div>

            {/* Active Panel */}
            {activePanel === 'upload' && (
              <BulkUploadPanel onUploadComplete={handleUploadComplete} />
            )}
            
            {activePanel === 'alerts' && (
              <AlertConfigurationPanel
                selectedItems={selectedItems}
                onSaveAlert={handleSaveAlert}
              />
            )}
            
            {activePanel === 'monitor' && (
              <JobMonitorPanel />
            )}
          </div>
        </div>

        {/* Mobile Panel Selector - Only visible on smaller screens */}
        <div className="xl:hidden mt-8">
          <div className="bg-surface border border-border rounded-lg shadow-card p-4 mb-6">
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant={activePanel === 'upload' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActivePanel('upload')}
                iconName="Upload"
                iconPosition="left"
              >
                Upload
              </Button>
              <Button
                variant={activePanel === 'alerts' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActivePanel('alerts')}
                iconName="Bell"
                iconPosition="left"
              >
                Alerts
              </Button>
              <Button
                variant={activePanel === 'monitor' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActivePanel('monitor')}
                iconName="Activity"
                iconPosition="left"
              >
                Jobs
              </Button>
            </div>
          </div>

          {/* Mobile Active Panel */}
          {activePanel === 'upload' && (
            <BulkUploadPanel onUploadComplete={handleUploadComplete} />
          )}
          
          {activePanel === 'alerts' && (
            <AlertConfigurationPanel
              selectedItems={selectedItems}
              onSaveAlert={handleSaveAlert}
            />
          )}
          
          {activePanel === 'monitor' && (
            <JobMonitorPanel />
          )}
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-surface border-t border-border mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Icon name="Crane" size={20} className="text-primary" />
              <span className="font-semibold text-text-primary">CranePrice</span>
            </div>
            <div className="text-sm text-text-secondary">
              © {new Date()?.getFullYear()} CranePrice. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InventoryManagement;